import React, { useState, useEffect, useRef } from "react";
import InterviewSetting2 from "./InterviewSetting2";
import { useObserver } from "mobx-react";
import { useStores } from "../../store";
import { MdInfoOutline } from "react-icons/md";
import { name } from "../../services/white_label";

const InterviewSettings = () => {
  const { RecuiterStore } = useStores();
  const [attemptsInputValue, setAttemptsInputValue] = useState(null);
  const attemptsInitialized = useRef(false);
  const interviewSetting = [
    {
      id: 9,
      label: "Resume",
      name: "resume",
      tooltip:
        "Automatically collect and attach the candidate's resume during the interview process for easy reference.",
    },
    {
      id: 2,
      label: "Screen Recording",
      name: "screen",
      tooltip:
        "Enable this to require candidates to share their screen during the interview, ensuring transparency and integrity.",
    },
    {
      id: 1,
      label: "Camera",
      name: "video",
      tooltip:
        "Enable this to record the candidate's video while they take the interview, helping recruiters assess their responses and body language.",
    },
    {
      id: 3,
      label: "Allow Typing",
      name: "typing",
      tooltip:
        "If enabled, candidates can type their responses in case of microphone issues or preference for written answers.",
    },
    {
      id: 4,
      label: "Mobile",
      name: "mobile",
      tooltip:
        "Turn this on to allow candidates to take the interview from mobile devices or mobile browsers.",
    },
    {
      id: 14,
      label: "Attempts",
      name: "attempts",
      tooltip:
        "Set the number of attempts candidates are allowed to take the assessment or interview.",
    },
    {
      id: 7,
      label: "Proxy Detection",
      name: "proxy",
      tooltip:
        "Activate AI-powered proxy detection to monitor candidate behavior and flag any suspicious activity during the interview.",
    },
    {
      id: 15,
      label: "Show report to the candidate",
      name: "showReport",
      tooltip:
        "Allow candidates to access their interview results and performance reports after completion.",
    },
    // {
    //   id: 5,
    //   label: "Reveal Actual answers",
    //   name: "actualAnswers",
    //   tooltip:
    //     "Enable this to let candidates view correct answers after completing their interview, useful for learning and feedback.",
    // },
    // {
    //   id: 6,
    //   label: "Show report to the candidate",
    //   name: "report",
    //   tooltip:
    //     "Allow candidates to access their interview results and performance reports after completion.",
    // },
    // {
    //   id: 8,
    //   label: `Show job post in ${name()}`,
    //   name: "showJobPost",
    //   tooltip:
    //     "Make this job post visible on the Reaidy job seeker portal, allowing candidates from the platform to apply directly.",
    // },
    // {
    //   id: 10,
    //   label: "Candidate pay for the interview",
    //   name: "candidatePay",
    //   tooltip:
    //     "If enabled, candidates will be required to pay for the interview instead of the recruiter covering the cost.",
    // },
    // {
    //   id: 11,
    //   label: "Allow to change tab",
    //   name: "tabChange",
    //   tooltip: "Allow candidates to change tabs during the interview.",
    // },
    // {
    //   id: 12,
    //   label: "Allow to full screen",
    //   name: "fullScreen",
    //   tooltip: "Allow candidates to full screen during the interview.",
    // },
    {
      id: 13,
      label: "Restrict Assessments",
      name: "restrictAssessments",
      tooltip:
        "Enable this to restrict candidates from taking assessments until you grant them access. You can provide access to specific candidates or to all candidates at once.",
    },
  ];

  const getLabel = (name) => {
    return interviewSetting.find((setting) => setting.name === name)?.label;
  };

  const getTooltip = (name) => {
    return interviewSetting.find((setting) => setting.name === name)?.tooltip;
  };

  // Determine if proxy is enabled
  const isProxyEnabled = RecuiterStore?.jobPostDetails?.customSettings?.find(
    (s) => s.name === "proxy"
  )?.value;

  // Helper to get setting object from customSettings by name
  const getCustomSetting = (name) =>
    RecuiterStore?.jobPostDetails?.customSettings?.find((s) => s.name === name);

  // Split settings for custom rendering order
  const beforeProxy = interviewSetting.filter(
    (s) =>
      s.name !== "proxy" &&
      !["tabChange", "fullScreen", "attempts", "restrictAssessments"].includes(
        s.name
      )
  );
  const proxySetting = interviewSetting.find((s) => s.name === "proxy");
  const attemptsSetting = interviewSetting.find((s) => s.name === "attempts");
  const restrictAssessmentsSetting = interviewSetting.find(
    (s) => s.name === "restrictAssessments"
  );
  const afterProxy = ["tabChange", "fullScreen"];

  // Initialize attempts setting with default value of 1 if it doesn't exist
  useEffect(() => {
    if (!attemptsSetting || !RecuiterStore?.jobPostDetails?.customSettings) {
      return;
    }

    // Double-check that setting doesn't exist before initializing
    const customSetting = RecuiterStore.jobPostDetails.customSettings.find(
      (s) => s.name === attemptsSetting.name
    );

    // Only initialize if setting doesn't exist and we haven't initialized yet
    if (!customSetting && !attemptsInitialized.current) {
      // Set the ref first to prevent concurrent calls
      attemptsInitialized.current = true;
      RecuiterStore.setJobPostCustomSettings(attemptsSetting.name, 1);
    } else if (customSetting) {
      // Mark as initialized if setting already exists
      attemptsInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptsSetting?.name]);

  return useObserver(() => (
    <div className="flex flex-col md:flex-row md:gap-10 w-[95%] mx-auto pb-1">
      <div className="container mx-auto md:pr-10 my-8 md:w-[65%] md:border-r md:border-gray-500 md:border-dashed">
        <h2 className="text-2xl font-bold text-orange-500 mb-6">
          Interview Settings
        </h2>
        <div className="grid md:grid-cols-3 md:gap-4 gap-2 md:mb-6 mb-3 grid-cols-1">
          {/* Render settings in order: Resume, Screen Recording, Camera, Allow Typing, Mobile */}
          {beforeProxy.map((setting, index) => {
            const customSetting = getCustomSetting(setting.name);
            const value = customSetting?.value || false;
            return (
              <div
                key={setting.name}
                className="bg-white rounded-lg border border-gray-300 p-3 flex items-center justify-between relative"
              >
                <div className="flex items-center gap-1">
                  <span className="text-gray-700 font-medium text-xs">
                    {setting.label}
                  </span>
                  {/* Info Icon for all options */}
                  <div className="group relative flex items-center">
                    <MdInfoOutline className="text-primary cursor-pointer" />
                    <div className="absolute hidden group-hover:flex items-center bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-[#EFEFEF] bg-opacity-100 z-20 text-black text-[12px] leading-5 p-3 rounded-lg w-60">
                      {setting.tooltip}
                      <div className="w-0 h-0 border-[10px] absolute -bottom-2 left-1/2 transform -translate-x-1/2 border-x-transparent border-t-[#EFEFEF] border-b-0"></div>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (setting.name === "mobile" && !value) {
                      // Check if proxy is enabled before allowing mobile access
                      if (isProxyEnabled) {
                        alert(
                          "Cannot enable mobile access while proxy detection is active. Please disable proxy detection first."
                        );
                        return;
                      }
                    }

                    RecuiterStore.setJobPostCustomSettings(
                      setting.name,
                      !value
                    );
                  }}
                  className={`w-8 h-4 flex items-center p-[2px] rounded-full transition-colors duration-300 ${value ? "bg-orange-500" : "bg-gray-300"
                    }`}
                >
                  <div
                    className={`w-3 h-3 bg-white rounded-full shadow-md transform transition-transform duration-300 ${value ? "translate-x-3.5" : "translate-x-0"
                      }`}
                  ></div>
                </button>
              </div>
            );
          })}

          {/* Render Attempts as number input - always visible */}
          {attemptsSetting &&
            (() => {
              const customSetting = getCustomSetting(attemptsSetting.name);
              // Always render attempts, use default value of 1 if setting doesn't exist
              const attemptsValue = customSetting?.value ?? 1;

              const displayValue =
                attemptsInputValue !== null
                  ? attemptsInputValue
                  : attemptsValue.toString();

              return (
                <div
                  key={attemptsSetting.name}
                  className="bg-white rounded-lg border border-gray-300 p-3 flex items-center justify-between relative"
                >
                  <div className="flex items-center gap-1">
                    <span className="text-gray-700 font-medium text-xs">
                      {attemptsSetting.label}
                    </span>
                    {/* Info Icon for attempts */}
                    <div className="group relative flex items-center">
                      <MdInfoOutline className="text-primary cursor-pointer" />
                      <div className="absolute hidden group-hover:flex items-center bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-[#EFEFEF] bg-opacity-100 z-20 text-black text-[12px] leading-5 p-3 rounded-lg w-60">
                        {attemptsSetting.tooltip}
                        <div className="w-0 h-0 border-[10px] absolute -bottom-2 left-1/2 transform -translate-x-1/2 border-x-transparent border-t-[#EFEFEF] border-b-0"></div>
                      </div>
                    </div>
                  </div>
                  <input
                    type="number"
                    min="1"
                    value={displayValue}
                    onBlur={(e) => {
                      const inputValue = e.target.value.trim();
                      const parsedValue = parseInt(inputValue);

                      if (
                        inputValue === "" ||
                        isNaN(parsedValue) ||
                        parsedValue < 1
                      ) {
                        setAttemptsInputValue(null);
                        RecuiterStore.setJobPostCustomSettings(
                          attemptsSetting.name,
                          1
                        );
                      } else {
                        setAttemptsInputValue(null);
                        RecuiterStore.setJobPostCustomSettings(
                          attemptsSetting.name,
                          parsedValue
                        );
                      }
                    }}
                    onChange={(e) => {
                      setAttemptsInputValue(e.target.value);
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              );
            })()}

          {/* Render proxy setting */}
          {proxySetting &&
            (() => {
              const customSetting = getCustomSetting(proxySetting.name);
              if (!customSetting) return null;
              return (
                <div
                  key={proxySetting.name}
                  className="bg-white rounded-lg border border-gray-300 p-3 flex items-center justify-between relative"
                >
                  <div className="flex items-center gap-1">
                    <span className="text-gray-700 font-medium text-xs">
                      {proxySetting.label}
                      <span className="ml-1 px-2 py-1 text-[8px] bg-blue-100 text-blue-600 rounded-full">
                        BETA
                      </span>
                    </span>
                    {/* Info Icon for proxy */}
                    <div className="group relative flex items-center">
                      <MdInfoOutline className="text-primary cursor-pointer" />
                      <div className="absolute hidden group-hover:flex items-center bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-[#EFEFEF] bg-opacity-100 z-20 text-black text-[12px] leading-5 p-3 rounded-lg w-60">
                        {proxySetting.tooltip}
                        <div className="w-0 h-0 border-[10px] absolute -bottom-2 left-1/2 transform -translate-x-1/2 border-x-transparent border-t-[#EFEFEF] border-b-0"></div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!customSetting.value) {
                        // If turning on proxy, automatically turn off mobile access
                        if (getCustomSetting("mobile")?.value) {
                          alert(
                            "Mobile access has been automatically disabled as it's not compatible with proxy detection."
                          );
                          RecuiterStore.setJobPostCustomSettings(
                            "mobile",
                            false
                          );
                        }
                        // When turning on proxy, automatically turn off screen, tabChange, and fullScreen
                        ["screen", "tabChange", "fullScreen"].forEach(
                          (name) => {
                            if (getCustomSetting(name)?.value) {
                              RecuiterStore.setJobPostCustomSettings(
                                name,
                                false
                              );
                            }
                          }
                        );
                      }
                      RecuiterStore.setJobPostCustomSettings(
                        proxySetting.name,
                        !customSetting.value
                      );
                    }}
                    className={`w-8 h-4 flex items-center p-[2px] rounded-full transition-colors duration-300 ${customSetting.value ? "bg-orange-500" : "bg-gray-300"
                      }`}
                  >
                    <div
                      className={`w-3 h-3 bg-white rounded-full shadow-md transform transition-transform duration-300 ${customSetting.value
                        ? "translate-x-3.5"
                        : "translate-x-0"
                        }`}
                    ></div>
                  </button>
                </div>
              );
            })()}

          {/* Render tabChange, fullScreen after proxy if proxy is enabled */}
          {isProxyEnabled &&
            afterProxy.map((name) => {
              const setting = interviewSetting.find((s) => s.name === name);
              const customSetting = getCustomSetting(name);
              if (!setting || !customSetting) return null;
              return (
                <div
                  key={setting.name}
                  className="bg-white rounded-lg border border-gray-300 p-3 flex items-center justify-between relative"
                >
                  <div className="flex items-center gap-1">
                    <span className="text-gray-700 font-medium text-xs">
                      {setting.label}
                    </span>
                    {/* Info Icon for all options */}
                    <div className="group relative flex items-center">
                      <MdInfoOutline className="text-primary cursor-pointer" />
                      <div className="absolute hidden group-hover:flex items-center bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-[#EFEFEF] bg-opacity-100 z-20 text-black text-[12px] leading-5 p-3 rounded-lg w-60">
                        {setting.tooltip}
                        <div className="w-0 h-0 border-[10px] absolute -bottom-2 left-1/2 transform -translate-x-1/2 border-x-transparent border-t-[#EFEFEF] border-b-0"></div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      RecuiterStore.setJobPostCustomSettings(
                        setting.name,
                        !customSetting.value
                      );
                    }}
                    className={`w-8 h-4 flex items-center p-[2px] rounded-full transition-colors duration-300 ${customSetting.value ? "bg-orange-500" : "bg-gray-300"
                      }`}
                  >
                    <div
                      className={`w-3 h-3 bg-white rounded-full shadow-md transform transition-transform duration-300 ${customSetting.value
                        ? "translate-x-3.5"
                        : "translate-x-0"
                        }`}
                    ></div>
                  </button>
                </div>
              );
            })}

          {/* Render Restrict Assessments last */}
          {restrictAssessmentsSetting &&
            (() => {
              const customSetting = getCustomSetting(
                restrictAssessmentsSetting.name
              );
              if (!customSetting) return null;
              return (
                <div
                  key={restrictAssessmentsSetting.name}
                  className="bg-white rounded-lg border border-gray-300 p-3 flex items-center justify-between relative"
                >
                  <div className="flex items-center gap-1">
                    <span className="text-gray-700 font-medium text-xs">
                      {restrictAssessmentsSetting.label}
                    </span>
                    {/* Info Icon for restrict assessments */}
                    <div className="group relative flex items-center">
                      <MdInfoOutline className="text-primary cursor-pointer" />
                      <div className="absolute hidden group-hover:flex items-center bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-[#EFEFEF] bg-opacity-100 z-20 text-black text-[12px] leading-5 p-3 rounded-lg w-60">
                        {restrictAssessmentsSetting.tooltip}
                        <div className="w-0 h-0 border-[10px] absolute -bottom-2 left-1/2 transform -translate-x-1/2 border-x-transparent border-t-[#EFEFEF] border-b-0"></div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      RecuiterStore.setJobPostCustomSettings(
                        restrictAssessmentsSetting.name,
                        !customSetting.value
                      );
                    }}
                    className={`w-8 h-4 flex items-center p-[2px] rounded-full transition-colors duration-300 ${customSetting.value ? "bg-orange-500" : "bg-gray-300"
                      }`}
                  >
                    <div
                      className={`w-3 h-3 bg-white rounded-full shadow-md transform transition-transform duration-300 ${customSetting.value
                        ? "translate-x-3.5"
                        : "translate-x-0"
                        }`}
                    ></div>
                  </button>
                </div>
              );
            })()}
        </div>
        {/* <div className="md:mt-6">
          <h2 className="text-2xl font-bold text-orange-500 mb-4">
            Schedule Assessment
          </h2>
          <div className="mb-4">
            <div className="md:flex items-center md:text-center gap-x-10 mb-4">
              <p className="text-base font-medium text whitespace-nowrap items-center flex gap-2">
                Do you want to schedule Assessment?
                <div className="group relative flex md:items-center gap-2">
                  <MdInfoOutline className="text-primary cursor-pointer" />

                  <div className="absolute hidden whitespace-nowrap group-hover:flex items-center bottom-full mb-3 left-1/2 transform -translate-x-1/2 bg-[#EFEFEF] bg-opacity-100 z-20 text-black text-[12px] leading-5 p-3 rounded-lg w-fit">
                    Between these above dates assessments will be done for all
                    candidates
                    <div className="w-0 h-0 border-[20px] absolute -bottom-2 left-[45%] border-x-[20px] border-transparent border-t-[#EFEFEF] border-b-0"></div>
                  </div>
                </div>
              </p>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <label
                      className="relative flex items-center cursor-pointer"
                      htmlFor="scheduleYes"
                    >
                      <input
                        name="schedule"
                        type="radio"
                        className="peer h-4 w-4 cursor-pointer appearance-none rounded-full border border-slate-300 checked:border-primary"
                        id="scheduleYes"
                        checked={
                          RecuiterStore?.jobPostDetails?.customSettings?.find(
                            (setting) => setting.name === "scheduleAssessment"
                          ).value
                        }
                        onChange={() =>
                          RecuiterStore.setJobPostCustomSettings(
                            "scheduleAssessment",
                            true
                          )
                        }
                      />
                      <span className="absolute bg-primary w-2 h-2 rounded-full opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                    </label>
                    <label className="ml-2 text-gray-800 font-medium">
                      Yes
                    </label>
                  </div>
                </div>
                <div className="flex items-center">
                  <label
                    className="relative flex items-center cursor-pointer"
                    htmlFor="scheduleNo"
                  >
                    <input
                      name="schedule"
                      type="radio"
                      className="peer h-4 w-4 cursor-pointer appearance-none rounded-full border border-slate-300 checked:border-primary"
                      id="scheduleNo"
                      checked={
                        !RecuiterStore.jobPostDetails.customSettings.find(
                          (setting) => setting.name === "scheduleAssessment"
                        ).value
                      }
                      onChange={() =>
                        RecuiterStore.setJobPostCustomSettings(
                          "scheduleAssessment",
                          false
                        )
                      }
                    />
                    <span className="absolute bg-primary w-2 h-2 rounded-full opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                  </label>
                  <label className="ml-2 text-gray-800 font-medium">No</label>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {RecuiterStore.jobPostDetails.customSettings.find(
          (setting) => setting.name === "scheduleAssessment"
        ).value && (
            <div>
              <div className="flex flex-wrap space-x-4 mb-2">
                {/* Assessment Start Date */}
                <div className="relative flex flex-col items-start">
                  <span className="text-sm font-semibold mb-1">
                    Assessment Start Date
                  </span>
                  <div className="relative flex items-center">
                    <input
                      type="date"
                      required
                      min={new Date().toISOString().split("T")[0]}
                      value={
                        RecuiterStore.jobPostDetails.customSettings.find(
                          (setting) => setting.name === "startDate"
                        ).value
                      }
                      className="border p-2 rounded w-40 focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      onChange={(e) => {
                        const date = e.target.value;
                        RecuiterStore.setJobPostCustomSettings("startDate", date);
                      }}
                    />
                  </div>
                </div>

                {/* Assessment End Date */}
                {RecuiterStore.jobPostDetails.customSettings.find(
                  (setting) => setting.name === "startDate"
                ).value && (
                    <div className="relative flex flex-col items-start">
                      <span className="text-sm font-semibold mb-1">
                        Assessment End Date
                      </span>
                      <div className="relative flex items-center">
                        <input
                          type="date"
                          min={
                            new Date(
                              new Date(
                                RecuiterStore.jobPostDetails.customSettings.find(
                                  (setting) => setting.name === "startDate"
                                ).value
                              ).setDate(
                                new Date(
                                  RecuiterStore.jobPostDetails.customSettings.find(
                                    (setting) => setting.name === "startDate"
                                  ).value
                                ).getDate() + 1
                              )
                            )
                              .toISOString()
                              .split("T")[0]
                          }
                          required
                          value={
                            RecuiterStore.jobPostDetails.customSettings.find(
                              (setting) => setting.name === "endDate"
                            ).value
                          }
                          className="border p-2 rounded w-40 focus:outline-none focus:ring-2 focus:ring-primary text-sm placeholder-gray-500"
                          onChange={(e) => {
                            const date = e.target.value;
                            RecuiterStore.setJobPostCustomSettings("endDate", date);
                          }}
                        />
                      </div>
                    </div>
                  )}
              </div>

              {/* Text Below the Options */}
              <div className="text-primary mt-2 text-sm font-medium">
                <p>
                  Between these above dates, assessments will be done for all
                  candidates.
                </p>
              </div>
            </div>
          )}
      </div>
      <div className="container mx-auto py-2 md:w-[30%]">
        <InterviewSetting2 />
      </div>
    </div>
  ));
};

export default InterviewSettings;
